@if (!loading()) {
  <div class="fullscreen-dialog-wrapper">
    <h1 mat-dialog-title>
      <span>{{ newRide ? 'Nieuwe rit voorbereiden' : 'Voorbereide rit bewerken' }}</span>
      <div class="title-buttons">
        @if (!newRide || savedRide) {
          <button mat-icon-button aria-label="Rit kopiëren" (click)="copyPreparedRide()" matTooltip="Rit kopiëren">
            <mat-icon>file_copy</mat-icon>
          </button>
          @if (!data.ride.retourParentId) {
            <button
              mat-icon-button
              aria-label="Retourrit maken"
              (click)="retourPreparedRide()"
              matTooltip="Retourrit maken"
            >
              <mat-icon>fast_rewind</mat-icon>
            </button>
          }
          <button
            mat-icon-button
            aria-label="Voorbereide rit verwijderen"
            (click)="deletePreparedRide()"
            matTooltip="Voorbereide rit verwijderen"
          >
            <mat-icon>delete</mat-icon>
          </button>
        }
        <button mat-icon-button aria-label="Sluiten" mat-dialog-close>
          <mat-icon>close</mat-icon>
        </button>
      </div>
    </h1>

    <div mat-dialog-content class="edit-ride-dialog-content">
      <form [formGroup]="rideForm" class="ride-form">
        <div class="dialog-grid">
          <!-- Left Column -->
          <div class="column">
            <div class="user-selection">
              <p class="form-title">Passagier zoeken</p>
              <mat-form-field class="search">
                <mat-label>Zoeken</mat-label>
                <input matInput [formControl]="searchCtrl" placeholder="Zoeken" [matAutocomplete]="userAutocomplete" />
                <mat-autocomplete #userAutocomplete="matAutocomplete">
                  @for (user of filteredUsers(); track user.id) {
                    <mat-option (click)="selectUser(user)" [value]="searchCtrl.value">
                      <div class="user-option">
                        <b>{{ user.name }}</b>
                        @if (user.streetName) {
                          <span> - {{ user.streetName }} {{ user.houseNumber }}, {{ user.city }}</span>
                        }
                      </div>
                    </mat-option>
                  }
                </mat-autocomplete>
                @if (searchCtrl.hasError('passengerRequired')) {
                  <mat-error>Dit veld is verplicht.</mat-error>
                }
              </mat-form-field>

              @if (passenger()) {
                <p class="selected-passenger-text">
                  {{ passenger()?.title }}
                  @if (passenger()?.address) {
                    <span> - {{ passenger()?.address }}</span>
                  }
                </p>
              }
            </div>

            @if (particularities.length > 0) {
              <p class="form-title">Bijzonderheden</p>
              <div class="icons">
                @for (particularity of particularities; track particularity) {
                  @if (particularity.id) {
                    <div
                      class="icon"
                      [ngClass]="{ active: rideForm.value.particularityIds.includes(particularity.id) }"
                      [matTooltip]="particularity.name"
                      role="button"
                      tabindex="0"
                      [attr.aria-label]="
                        rideForm.value.particularityIds.includes(particularity.id)
                          ? 'Bijzonderheid deselecteren: ' + particularity.name
                          : 'Bijzonderheid selecteren: ' + particularity.name
                      "
                      [attr.aria-pressed]="rideForm.value.particularityIds.includes(particularity.id)"
                      (click)="toggleParticularity(particularity.id)"
                      (keydown.enter)="toggleParticularity(particularity.id)"
                      (keydown.space)="$event.preventDefault(); toggleParticularity(particularity.id)"
                    >
                      <mat-icon [svgIcon]="particularity.id"></mat-icon>
                    </div>
                  }
                }
              </div>
            }
            @if (useCharacteristics) {
              <p class="form-title">Kenmerken</p>
              <div class="characteristics-wrapper">
                <mat-form-field class="characteristics-field mat-form-field-no-subscript">
                  <mat-label>Kenmerken</mat-label>
                  <mat-chip-grid #chipGrid required>
                    @for (char of characteristics(); track char) {
                      <mat-chip-row (removed)="removeCharacteristic(char)">
                        {{ char }}
                        <button matChipRemove><mat-icon>cancel</mat-icon></button>
                      </mat-chip-row>
                    }
                    <input
                      placeholder="Nieuw kenmerk..."
                      #characteristicInput
                      [formControl]="characteristicCtrl"
                      [matAutocomplete]="auto"
                      [matChipInputFor]="chipGrid"
                      [matChipInputSeparatorKeyCodes]="separatorKeysCodes"
                      (matChipInputTokenEnd)="addCharacteristic($event)"
                    />
                  </mat-chip-grid>
                  <mat-autocomplete #auto="matAutocomplete" (optionSelected)="selectedCharacteristic($event)">
                    @for (characteristic of filteredCharacteristics(); track characteristic) {
                      <mat-option [value]="characteristic">
                        {{ characteristic }}
                      </mat-option>
                    }
                  </mat-autocomplete>
                </mat-form-field>
                @if (characteristics().length < 1 && characteristicCtrl.touched) {
                  <mat-error>Dit veld is verplicht.</mat-error>
                }
              </div>
            }
            <div class="capacity-section">
              <div class="capacity">
                <div class="form-subtitle">Capaciteitsmeter: {{ capacityStatus() }}</div>
                <meter
                  [style.--meter-bg]="capacityMeterBg()"
                  min="0"
                  max="1"
                  optimum="0"
                  low="0.45"
                  high="0.75"
                  [value]="capacityMeter()"
                  matTooltip="Hoe drukker de (voorbereide) planning is, hoe kleiner de kans dat het automatisch plannen lukt."
                  matTooltipClass="wide-tooltip"
                ></meter>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="column">
            <div class="date-capacity-row">
              <div class="date-section">
                <p class="form-title">Datum en tijd</p>
                <mat-form-field class="date">
                  <mat-label>Datum</mat-label>
                  <input
                    matInput
                    [matDatepicker]="datePicker"
                    formControlName="rideDate"
                    required
                    (dateChange)="dateUpdated('date', $event.value)"
                  />
                  <mat-datepicker-toggle
                    matSuffix
                    aria-label="Kalender openen"
                    [for]="datePicker"
                  ></mat-datepicker-toggle>
                  <mat-datepicker #datePicker></mat-datepicker>
                  @if (rideForm.controls.rideDate.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                </mat-form-field>
              </div>
              <div class="capacity-section">
                <mat-slide-toggle formControlName="specifyDropoffTime">Afzettijd opgeven</mat-slide-toggle>
                <button
                  mat-flat-button
                  class="secondary-color"
                  [disabled]="!rideForm.getRawValue().from || !rideForm.getRawValue().to"
                  (click)="onMapsClick($event, getRideData(true))"
                >
                  Route bekijken
                </button>
              </div>
            </div>

            <div class="times-row">
              <div class="time-column">
                <div class="dropoff-header">
                  <p class="form-subtitle">Ophaaltijd</p>
                </div>
                <div class="datetime">
                  <mat-form-field class="hour">
                    <mat-label>Uur</mat-label>
                    <mat-select formControlName="startHour" (selectionChange)="dateUpdated('start')">
                      @for (hour of hours(); track hour) {
                        <mat-option [value]="hour">{{ hour }}</mat-option>
                      }
                    </mat-select>
                    @if (rideForm.controls.startHour.hasError('required')) {
                      <mat-error>Dit veld is verplicht.</mat-error>
                    }
                  </mat-form-field>
                  <mat-form-field class="minute">
                    <mat-label>Minuut</mat-label>
                    <mat-select formControlName="startMinute" (selectionChange)="dateUpdated('start')">
                      @for (min of minutes(); track min) {
                        <mat-option [value]="min.val">{{ min.text }}</mat-option>
                      }
                    </mat-select>
                    @if (rideForm.controls.startMinute.hasError('required')) {
                      <mat-error>Dit veld is verplicht.</mat-error>
                    }
                  </mat-form-field>
                </div>
              </div>

              <div class="time-column">
                <div class="dropoff-header">
                  @if (showDropoffTime()) {
                    <p class="form-subtitle">Afzettijd</p>
                  } @else {
                    <p class="form-subtitle" style="visibility: hidden">Afzettijd</p>
                  }
                </div>
                @if (showDropoffTime()) {
                  <div class="datetime">
                    <mat-form-field class="hour">
                      <mat-label>Uur</mat-label>
                      <mat-select formControlName="endHour" (selectionChange)="dateUpdated('end')">
                        @for (hour of hours(); track hour) {
                          <mat-option [value]="hour">{{ hour }}</mat-option>
                        }
                      </mat-select>
                      @if (rideForm.controls.endHour.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                    <mat-form-field class="minute">
                      <mat-label>Minuut</mat-label>
                      <mat-select formControlName="endMinute" (selectionChange)="dateUpdated('end')">
                        @for (min of minutes(); track min) {
                          <mat-option [value]="min.val">{{ min.text }}</mat-option>
                        }
                      </mat-select>
                      @if (rideForm.controls.endMinute.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                  </div>
                }
              </div>
            </div>

            <p class="form-title">Locatie</p>
            <div class="location-grid">
              <div class="location-box">
                <mat-form-field class="type-select">
                  <mat-label>Ophalen</mat-label>
                  <mat-select formControlName="fromType" required (selectionChange)="onInputTypeChange('from', $event)">
                    @for (type of locationTypes; track type) {
                      <mat-option [value]="type.val">
                        {{ type.name }}
                      </mat-option>
                    }
                  </mat-select>
                  @if (rideForm.controls.fromType.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                </mat-form-field>
                <div class="address-container">
                  @if (!fromHidden()) {
                    <app-ors-address-autocomplete
                      formControlName="from"
                      [label]="'Adres'"
                      [placeholder]="'mijn straat 1, mijn dorp'"
                      [required]="true"
                      [autoActiveFirstOption]="true"
                    ></app-ors-address-autocomplete>
                  }
                  @if (!standardFromHidden()) {
                    <mat-form-field>
                      <mat-label>Adres</mat-label>
                      <input
                        #fromStandardAddressInput
                        type="search"
                        formControlName="fromSearch"
                        matInput
                        [matAutocomplete]="fromStandardAddressAuto"
                      />
                      <mat-autocomplete
                        #fromStandardAddressAuto="matAutocomplete"
                        (optionSelected)="standardAddressSelected($event, 'from')"
                      >
                        @for (address of addressesFrom(); track address) {
                          <mat-option [value]="address.id"> {{ address.name }}, {{ address.address }} </mat-option>
                        }
                        @if (nothingFound()) {
                          <mat-option class="no-options" disabled> Geen overeenkomende adressen gevonden </mat-option>
                        }
                      </mat-autocomplete>
                      @if (rideForm.controls.from.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                  }
                  @if (!recentFromHidden()) {
                    <mat-form-field>
                      <mat-label>Adres</mat-label>
                      <mat-select formControlName="from" required>
                        @for (destination of recentDestinations(); track destination) {
                          <mat-option class="recent-destination" [value]="destination">
                            <span>{{ destination }}</span>
                          </mat-option>
                        }
                      </mat-select>
                      @if (rideForm.controls.from.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                  }
                </div>
              </div>

              <div class="location-box">
                <mat-form-field class="type-select">
                  <mat-label>Afzetten</mat-label>
                  <mat-select formControlName="toType" required (selectionChange)="onInputTypeChange('to', $event)">
                    @for (type of locationTypes; track type) {
                      <mat-option [value]="type.val">
                        {{ type.name }}
                      </mat-option>
                    }
                  </mat-select>
                  @if (rideForm.controls.toType.hasError('required')) {
                    <mat-error>Dit veld is verplicht.</mat-error>
                  }
                </mat-form-field>
                <div class="address-container">
                  @if (!toHidden()) {
                    <app-ors-address-autocomplete
                      formControlName="to"
                      [label]="'Adres'"
                      [placeholder]="'mijn straat 1, mijn dorp'"
                      [required]="true"
                      [autoActiveFirstOption]="true"
                    ></app-ors-address-autocomplete>
                  }
                  @if (!standardToHidden()) {
                    <mat-form-field>
                      <mat-label>Adres</mat-label>
                      <input
                        #toStandardAddressInput
                        type="search"
                        formControlName="toSearch"
                        matInput
                        [matAutocomplete]="toStandardAddressAuto"
                      />
                      <mat-autocomplete
                        #toStandardAddressAuto="matAutocomplete"
                        (optionSelected)="standardAddressSelected($event, 'to')"
                      >
                        @for (address of addressesTo(); track address) {
                          <mat-option [value]="address.id"> {{ address.name }}, {{ address.address }} </mat-option>
                        }
                        @if (nothingFound()) {
                          <mat-option class="no-options" disabled> Geen overeenkomende adressen gevonden </mat-option>
                        }
                      </mat-autocomplete>
                      @if (rideForm.controls.to.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                  }
                  @if (!recentToHidden()) {
                    <mat-form-field>
                      <mat-label>Adres</mat-label>
                      <mat-select formControlName="to" required>
                        @for (destination of recentDestinations(); track destination) {
                          <mat-option class="recent-destination" [value]="destination">
                            <span>{{ destination }}</span>
                          </mat-option>
                        }
                      </mat-select>
                      @if (rideForm.controls.to.hasError('required')) {
                        <mat-error>Dit veld is verplicht.</mat-error>
                      }
                    </mat-form-field>
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="remarks-row">
          <div class="remarks-column">
            <p class="form-title">Opmerkingen</p>
            <mat-form-field>
              <mat-label>Opmerking (Planner)</mat-label>
              <textarea formControlName="remarks" matInput rows="3"></textarea>
              <mat-hint>Optioneel</mat-hint>
            </mat-form-field>
          </div>
          <div class="remarks-column">
            <p class="form-title" style="visibility: hidden">Spacer</p>
            <mat-form-field>
              <mat-label>Opmerking (Voor chauffeur)</mat-label>
              <textarea formControlName="commentsForDriver" matInput rows="3"></textarea>
              <mat-hint>Optioneel</mat-hint>
            </mat-form-field>
          </div>
        </div>
      </form>
    </div>

    <div mat-dialog-actions>
      <button mat-button mat-dialog-close>{{ newRide ? 'Annuleren' : 'Sluiten' }}</button>
      <button mat-flat-button color="primary" [disabled]="isSaveDisabled()" [class.spinner]="saving()" (click)="save()">
        Opslaan
      </button>
    </div>
  </div>
} @else {
  <div class="content-spinner">
    <mat-spinner></mat-spinner>
  </div>
}
